import React, {useEffect, useState} from "react";
import {Button, Col, Container, Form, OverlayTrigger, Row, Tooltip} from "react-bootstrap";
import DataTable from 'react-data-table-component';
import ApiService from "../services/ApiService";
import {Link, useNavigate} from "react-router-dom";
import EventObserver from "../utils/EventObserver";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faArrowRotateBack,
    faEdit,
    faExclamationTriangle,
    faAddressCard,
    faPlus,
    faQrcode
} from '@fortawesome/pro-light-svg-icons';
import Multiselect from 'multiselect-react-dropdown';
import AlertShow from "../components/AlertShow";
import {getUser, getToken} from "../services/AuthService";
import {newsletters, statuses} from "../utils/reusableText";

const CustomCell = (row) => {
    if (row.status === statuses.registered) {
        const tooltip = (
            <Tooltip id="tooltip">
                In attesa di abilitazione
            </Tooltip>
        );

        return (
            <div className="d-flex gap-2 w-100 justify-content-end pe-2">
                <OverlayTrigger placement="top" overlay={tooltip}>
                    <div
                        className="triangle-icon text-warning"
                        style={{width: '1.5em', height: '1.5em', lineHeight: '1.5em', textAlign: 'center'}}
                    >
                        <FontAwesomeIcon icon={faExclamationTriangle}/>
                    </div>
                </OverlayTrigger>
            </div>
        );
    } else {
        return null;
    }
};

const Homepage = () => {
    const currentUser = getUser();
    const navigate = useNavigate();

    const paginationOptions = {
        noRowsPerPage: true,
        rangeSeparatorText: 'di',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Tutte',
    };

    const columns = [
        {
            name: 'Nome/Azienda',
            selector: row => row.firstName,
            sortable: true,
        },
        {
            name: 'Cognome/Azienda',
            selector: row => row.lastName,
            sortable: true,
        },
        {
            name: 'Email',
            selector: row => row.email,
            sortable: true,
        },
        {
            name: 'Numero cliente',
            selector: row => row.clientNumber,
            sortable: true,
        },
        {
            name: 'Tipologia abbonamento',
            selector: row => row.subscriptions?.map(sub => sub.description).join('\n') ?? '',
                cell: row => (
                    <div>
                        {row.subscriptions?.map((sub, index) => (
                            <div key={index}>{sub.description}</div>
                        ))}
                    </div>
                ),
            sortable: true,
        },
        {
            name: 'Data attivazione',
            selector: row => row.subscriptions?.map(sub => sub.activationDate).join('\n') ?? '',
                cell: row => (
                    <div>
                        {row.subscriptions?.map((sub, index) => (
                            <div key={index}>{sub.activationDate}</div>
                        ))}
                    </div>
                ),
            sortable: true,
        },
        {
            name: 'Data scadenza',
            selector: row => row.subscriptions?.map(sub => sub.expirationDate).join('\n') ?? '',
                cell: row => (
                    <div>
                        {row.subscriptions?.map((sub, index) => (
                            <div key={index}>{sub.expirationDate}</div>
                        ))}
                    </div>
                ),
            sortable: true,
        },
        {
            name: '',
            selector: row => row.status,
            cell: (row) => CustomCell(row),
            width: '1em',
        },
        {
            name: '',
            button: true,
            selector: row => row.id,
            cell: (row) => {
                const hasMasi = row.subscriptions?.some(sub => sub.description?.toUpperCase().includes('MASI'));
                const hasLac = row.subscriptions?.some(sub => sub.description?.toUpperCase().includes('LAC'));
                const isEnabled = row.subscriptions.length > 0 && row.status !== statuses.disabled;

                return (
                    <div className={'d-flex gap-2 w-100 justify-content-end pe-2'}>
                        <Button variant={'link'} size={'sm'} className={'p-0'} onClick={() => navigate('/fe/edit-user/' + row.id)}>
                            <FontAwesomeIcon icon={faEdit}/>
                        </Button>

                        {isEnabled &&
                            <>
                                <Button variant={'link'} size={'sm'} className={'p-0'} onClick={() => downloadQrcode(row)}>
                                    <FontAwesomeIcon icon={faQrcode}/>
                                </Button>

                                {hasLac &&
                                    <Button variant={'link'} size={'sm'} className={'p-0 position-relative'} onClick={() => downloadPdf(row,'lac')}>
                                        <FontAwesomeIcon icon={faAddressCard}/>
                                        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger text-white"
                                            style={{ fontSize: '0.6rem' }}>L</span>
                                    </Button>
                                }
                                {hasMasi &&
                                    <Button variant={'link'} size={'sm'} className={'p-0 position-relative'} onClick={() => downloadPdf(row, 'masi')}>
                                        <FontAwesomeIcon icon={faAddressCard} className="text-danger"/>
                                        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger text-white"
                                            style={{ fontSize: '0.6rem' }}>M</span>
                                    </Button>
                                }
                            </>
                        }
                    </div>
                );
            },
        },
    ];

    const [listOfUsers, setListOfUsers] = useState([]);
    const [listOfSubscriptions, setListOfSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [query, setQuery] = useState('');
    const [dataType, setDataType] = useState(undefined);
    const [expired, setExpired] = useState(false);
    const [selectedSubs, setSelectedSubs] = useState([]);
    const [selectedNewsletter, setSelectedNewsletter] = useState([]);
    const [showAlert, setShowAlert] = useState(false);
    const [showAlertExcel, setShowAlertExcel] = useState(false);
    const [textAlert, setTextAlert] = useState('Qualcosa è andato storto. Per favore, riprova!');
    const [totalRows, setTotalRows] = useState(0);
    const [pageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [filterCounter, setFilterCounter] = useState(0);
    const [currentSort, setCurrentSort] = useState({
        sortField: undefined,
        sort: undefined,
    });
    const [totalElements, setTotalElements] = useState(undefined)
    const [keycloakReady, setKeycloakReady] = useState(false);

    useEffect(() => {
        // Check if Keycloak is already ready (token exists in localStorage)
        const token = getToken();
        if (token) {
            setKeycloakReady(true);
            fetchUsers();
            fetchSubs();
        } else {
            // If not ready yet, listen for the event
            EventObserver.on("keycloakReady", (data) => {
                if (data) {
                    setKeycloakReady(true);
                    fetchUsers();
                    fetchSubs();
                }
            });
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setFilterCounter(filterCounter + 1);
        // eslint-disable-next-line
    }, [query, expired, selectedSubs, dataType, from, to]);

    useEffect(() => {
        if (keycloakReady && ((query !== '' && query.length > 2) || query === '' || selectedSubs.length > 0 || expired)) {
            fetchUsers();
        }
        // eslint-disable-next-line
    }, [query, expired, selectedSubs, currentSort]);

    function fetchSubs() {
        ApiService.getAllSubscription().then(response => {
            const allValue = {
                id: 0,
                code: "ALL",
                label: "TUTTE",
                description: "ALL"
            }
            response.push(allValue);
            setListOfSubscriptions(response);
        });
    }

    function downloadQrcode(user) {
        ApiService.getQrCodeByUuid(user.uuid)
            .then(response => response.blob())
            .then(blob => {
                const url = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'Card ' + user.firstName + ' ' + user.lastName + '.png');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                URL.revokeObjectURL(url);
            })
            .catch(error => {
                setTextAlert(error?.message || 'Qualcosa è andato storto. Per favore, riprova!');
                setShowAlert(true);
            });
    }

    function downloadPdf(user, template) {
        ApiService.getPdfByUuid(user.uuid, template)
            .then(response => response.blob())
            .then(blob => {
                const url = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'pdfCard - ' + user.firstName + ' ' + user.lastName + '.pdf');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                URL.revokeObjectURL(url);
            })
            .catch(error => {
                setTextAlert(error?.message || 'Qualcosa è andato storto. Per favore, riprova!');
                setShowAlert(true);
            });
    }

    function handlePageChange(newPage) {
        fetchUsers(newPage);
    }

    function getExcel() {
        const arrSubs = selectedSubs.map(sub => sub.id);

        const body = {
            from: from !== '' ? from : undefined,
            to: to !== '' ? to : undefined,
            dateType: dataType,
            query: query !== '' ? query : undefined,
            expired: expired,
            subscriptionIds: arrSubs.length > 0 ? arrSubs : undefined,
            newsletter: selectedNewsletter.length > 0 && selectedNewsletter.some(newsletter => newsletter.value === 'newsletter') === true ? true : undefined,
            newsletter_1: selectedNewsletter.length > 0 && selectedNewsletter.some(newsletter => newsletter.value === 'newsletter_1') === true ? true : undefined,
            newsletter_2: selectedNewsletter.length > 0 && selectedNewsletter.some(newsletter => newsletter.value === 'newsletter_2') === true ? true : undefined
        };

        if (totalElements > 0 && totalElements < 30) {
            ApiService.exportToExcel(body)
                .then(response => response.blob())
                .then(blob => {

                    const fileURL = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = fileURL;

                    const currDate = new Date();
                    const day = currDate.getDate().toString().padStart(2, '0');
                    const month = (currDate.getMonth() + 1).toString().padStart(2, '0');
                    const year = currDate.getFullYear().toString();
                    const formattedDate = `${day}/${month}/${year}`;

                    link.setAttribute('download', 'users_export_' + formattedDate + '.xlsx');
                    document.body.appendChild(link);
                    link.click();
                })
                .catch(e => {
                    setTextAlert(e?.message || 'Qualcosa è andato storto. Per favore, riprova!');
                    setShowAlert(true);
                });
        } else {
            ApiService.exportToExcel(body).then(response => {
                if (response.ok) {
                    setShowAlertExcel(true);
                } else {
                    setShowAlert(true);
                }
            });
        }
    }

    function fetchUsers(newPage: undefined) {
        setLoading(true);

        const arrSubs = selectedSubs.map(sub => sub.id);

        const searchBody = {
            from: from !== '' ? from : undefined,
            to: to !== '' ? to : undefined,
            dateType: dataType,
            query: query !== '' ? query : undefined,
            expired: expired,
            subscriptionIds: arrSubs.length > 0 ? arrSubs : undefined,
            newsletter: selectedNewsletter.length > 0 && selectedNewsletter.some(newsletter => newsletter.value === 'newsletter') === true ? true : undefined,
            newsletter_1: selectedNewsletter.length > 0 && selectedNewsletter.some(newsletter => newsletter.value === 'newsletter_1') === true ? true : undefined,
            newsletter_2: selectedNewsletter.length > 0 && selectedNewsletter.some(newsletter => newsletter.value === 'newsletter_2') === true ? true : undefined
        };

        if (newPage) {
            setPage(newPage)
        }

        ApiService.searchByFilters(searchBody, newPage || page, pageSize, currentSort.sort, currentSort.sortField).then(response => {
            setLoading(false);
            setTotalRows(response.totalElements)
            setListOfUsers(response.users);
            setTotalElements(response.totalElements);
        }).catch(error => {
            setTextAlert(error?.message || 'Qualcosa è andato storto. Per favore, riprova!');
            setShowAlert(true);
        });

    }

    function resetFilters() {
        setFrom('');
        setTo('');
        setQuery('');
        setDataType(undefined);
        setExpired(false);
        setSelectedSubs([]);
        setSelectedNewsletter([]);
        setPage(1);
        setTotalRows(0);
        setListOfUsers([]);
        setCurrentSort({});

        fetchUsers();
    }

    function handleSort(column, sort) {
        let sortField = '';
        const stringColumn = column.toString();

        if (stringColumn.includes("activationDate")) {
            sortField = 'subscriptions.activationDate';
        } else if (stringColumn.includes("expirationDate")) {
            sortField = 'subscriptions.expirationDate';
        } else if (stringColumn.includes("description")) {
            sortField = 'subscriptions.subscription.code';
        } else if (stringColumn.includes("firstName")) {
            sortField = 'firstName';
        } else if (stringColumn.includes("lastName")) {
            sortField = 'lastName';
        } else if (stringColumn.includes("email")) {
            sortField = 'email';
        } else {
            sortField = 'clientNumber';
        }

        if (sortField === currentSort.sortField) {
            const newSortDirection = currentSort.sortDirection === "asc" ? "desc" : "asc";
            setCurrentSort({sortField, sort: newSortDirection});
        } else {
            setCurrentSort({sortField, sortDirection: "asc"});
        }

        setCurrentSort(prevSort => ({...prevSort, sortField, sort}));
    }

    function checkSearch() {
        return !((dataType && from !== '' && to !== '') || selectedNewsletter.length > 0)
    }

    return (
        <>
            {currentUser?.role === 'admin' || currentUser?.role === 'superAdmin' || currentUser?.role === 'adminNoExport' ?
                <Row>
                    <Col>
                        <div className={'content-main'}>
                            <Row>
                                <Col lg={4}>
                                    <Form.Group className="mb-1" controlId="query">
                                        <Form.Label>Cerca</Form.Label>
                                        <Form.Control name="query" type="text" size={'sm'}
                                                      value={query}
                                                      onChange={(e) => {
                                                          setQuery(e.target.value);
                                                          setPage(1);
                                                      }}/>
                                    </Form.Group>
                                </Col>
                                <Col lg={2}>
                                    <div className={'d-flex flex-column h-100'}>
                                        <div className="form-check mt-4 pt-1">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name={'expired'}
                                                checked={expired}
                                                onChange={(e) => {
                                                    setExpired(!expired);
                                                    setPage(1);
                                                }}
                                                id={'membership'}
                                            />
                                            <label className="form-check-label small" htmlFor={'membership'}>Membership
                                                scadute</label>
                                        </div>
                                    </div>
                                </Col>
                                <Col lg={{span: 2, offset: 1}} className={'ms-auto'}>
                                    <Link to={"/fe/edit-user"} className={'btn btn-primary w-100'}><FontAwesomeIcon
                                        icon={faPlus}
                                        className={'me-1'}/>Nuovo
                                        utente</Link>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg={6}>
                                    <Form.Label>Tipologia abbonamento</Form.Label>
                                    <Multiselect
                                        className={'mb-1'}
                                        options={listOfSubscriptions}
                                        selectedValues={selectedSubs}
                                        onSelect={(newValue) => {
                                            setSelectedSubs(newValue);
                                            setPage(1);
                                        }}
                                        onRemove={setSelectedSubs}
                                        displayValue="label"
                                        closeIcon="cancel"
                                        placeholder={null}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col lg={6}>
                                    <hr className={'mt-2 mb-1'}/>
                                </Col>
                            </Row>

                            <Row>
                                <Col sm={'auto'}>
                                    <div className={'d-flex flex-column justify-content-end h-100'}>
                                        <div className="form-check mb-4">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name={'dataTypeActivation'}
                                                checked={dataType === 'activationDate'}
                                                onChange={(e) => setDataType('activationDate')}
                                                id={'activation'}
                                            />
                                            <label className="form-check-label small"
                                                   htmlFor={'activation'}>Attivazione</label>
                                        </div>
                                    </div>
                                </Col>
                                <Col sm={'auto'}>
                                    <div className={'d-flex flex-column justify-content-end h-100'}>
                                        <div className="form-check mb-4">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name={'dataTypeExpiration'}
                                                checked={dataType === 'expirationDate'}
                                                onChange={(e) => setDataType('expirationDate')}
                                                id={'expiration'}
                                            />
                                            <label className="form-check-label small"
                                                   htmlFor={'expiration'}>Scadenza</label>
                                        </div>
                                    </div>
                                </Col>
                                <Col lg={2}>
                                    <Form.Group className="mb-3" controlId="from">
                                        <Form.Label>Data dal</Form.Label>
                                        <Form.Control type="date" name="from" value={from} size={'sm'}
                                                      onChange={(e) => setFrom(e.target.value)}
                                                      disabled={!dataType}/>
                                    </Form.Group>
                                </Col>
                                <Col lg={2}>
                                    <Form.Group className="mb-3" controlId="to">
                                        <Form.Label>Data al</Form.Label>
                                        <Form.Control type="date" name="to" value={to} size={'sm'}
                                                      onChange={(e) => setTo(e.target.value)} disabled={!dataType}/>
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Label>Newsletter</Form.Label>
                                    <Multiselect
                                        options={[
                                            {id: 0, label: newsletters.newsletter, value: 'newsletter'},
                                            {id: 1, label: newsletters.newsletter_1, value: 'newsletter_1'},
                                            {id: 2, label: newsletters.newsletter_2, value: 'newsletter_2'},
                                        ]}

                                        selectedValues={selectedNewsletter}
                                        onSelect={(selectedList, selectedItem) => {
                                            setSelectedNewsletter([...selectedList]);
                                            setPage(1);
                                        }}
                                        displayValue="label"
                                        closeIcon="cancel"
                                        placeholder={null}
                                        onRemove={(selectedList, removedItem) => {
                                            const updatedSelection = selectedList.filter(item => item.id !== removedItem.id);
                                            setSelectedNewsletter([...updatedSelection]);
                                            setPage(1);
                                        }}
                                    />
                                </Col>
                                <Col lg={2}>
                                    <div className={'d-flex flex-column justify-content-end h-100'}>
                                        <Button variant={'primary'} className={'w-100 mb-3'} size={'sm'}
                                                onClick={() => {
                                                    fetchUsers();
                                                    setPage(1);
                                                }}
                                                disabled={checkSearch()}>Cerca</Button>
                                    </div>
                                </Col>
                                <Col lg={'auto'}>
                                    <div className={'d-flex flex-column h-100'}>
                                        <Button variant={'link'} className={'p-0 text-decoration-none mt-4 pt-1'}
                                                size={'sm'}
                                                onClick={() => resetFilters()}>
                                            <FontAwesomeIcon icon={faArrowRotateBack} className={'me-1'}/>Reset
                                        </Button>
                                    </div>
                                </Col>

                            </Row>

                            <Row className={'my-3'}>
                                <Col lg={12}>
                                    <DataTable
                                        key={filterCounter}
                                        columns={columns}
                                        data={listOfUsers}
                                        highlightOnHover
                                        progressPending={loading}
                                        pagination
                                        paginationServer
                                        paginationComponentOptions={paginationOptions}
                                        paginationPerPage={pageSize}
                                        paginationTotalRows={totalRows}
                                        onChangePage={handlePageChange}
                                        onSort={(column, direction) => handleSort(column.selector, direction)}
                                    />
                                </Col>
                            </Row>
                            {(currentUser?.role === 'admin' || currentUser?.role === 'superAdmin') &&
                            <Row>
                                <Col lg={2}>
                                    <Button variant={'secondary'} className={'w-100'} onClick={() => getExcel()}
                                            disabled={listOfUsers.length === 0}>Esporta
                                        Excel</Button>
                                </Col>
                            </Row>
                            }
                        </div>
                    </Col>
                </Row>
                :
                <Container className="d-flex justify-content-center align-items-top vh-100">
                    <div className="text-center bs-font-sans-serif">
                        <h5>Alla tua utenza non sono associati permessi per visualizzare i contenuti</h5>
                    </div>
                </Container>
            }

            <AlertShow show={showAlert} setShow={setShowAlert} variant={"danger"} title={"Errore"}
                       content={textAlert}></AlertShow>

            <AlertShow show={showAlertExcel} setShow={setShowAlertExcel} variant={"success"} title={"Fatto"}
                       content={"Elaborazione in corso. Riceverai il file via email."}></AlertShow>
        </>
    );
}

export default Homepage;